import { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { api, getToken } from '../../api';
import LocationSearch from '../../components/LocationSearch';
import { TOTAL_ONBOARDING_STEPS } from './constants';

const MONTHS = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];
const days  = Array.from({ length: 31 }, (_, i) => i + 1);
const years = Array.from({ length: 80 }, (_, i) => new Date().getFullYear() - 18 - i);
const hours = Array.from({ length: 24 }, (_, i) => i);
const mins  = Array.from({ length: 12 }, (_, i) => i * 5);

export default function BirthData() {
  const navigate = useNavigate();
  const location = useLocation();
  const fromSettings = location.state?.fromSettings;
  const [form, setForm] = useState({
    day: '', month: '', year: '',
    hour: '12', min: '0',
    unknownTime: false,
  });
  const [birthCoords, setBirthCoords] = useState(null);
  const [existingCoords, setExistingCoords] = useState(null);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [profileLoaded, setProfileLoaded] = useState(!fromSettings);

  useEffect(() => {
    if (!getToken()) { navigate('/login'); return; }
    if (!fromSettings) return;
    api.get('/profile').then(p => {
      if (p.birth_day) {
        setForm(f => ({
          ...f,
          day: String(p.birth_day),
          month: String(p.birth_month),
          year: String(p.birth_year),
          hour: p.birth_hour != null ? String(p.birth_hour) : '12',
          min: p.birth_min != null ? String(p.birth_min) : '0',
        }));
      }
      if (p.birth_lat && p.birth_lon) {
        setExistingCoords({ lat: parseFloat(p.birth_lat), lng: parseFloat(p.birth_lon), tzone: parseFloat(p.birth_tzone) || 0 });
      }
      setProfileLoaded(true);
    }).catch(() => setProfileLoaded(true));
  }, [navigate, fromSettings]);

  if (!getToken()) { navigate('/login'); return null; }

  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    const coords = birthCoords || existingCoords;
    if (!coords) {
      setError('Please select a city from the suggestions.');
      return;
    }

    setLoading(true);
    try {
      await api.post('/birth-data', {
        day:   parseInt(form.day),
        month: parseInt(form.month),
        year:  parseInt(form.year),
        hour:  form.unknownTime ? 12 : parseInt(form.hour),
        min:   form.unknownTime ? 0  : parseInt(form.min),
        lat:   coords.lat,
        lon:   coords.lng,
        tzone: coords.tzone,
      });
      navigate(fromSettings ? '/settings' : '/onboarding/preferences');
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  if (!profileLoaded) {
    return <div style={{ height: '100vh', background: '#000' }} />;
  }

  return (
    <div className="auth-screen" style={{ paddingTop: 60 }}>
      {!fromSettings && <div className="step-indicator">2 of {TOTAL_ONBOARDING_STEPS}</div>}
      <div className="display-md">When and where you began</div>
      <div style={{
        fontFamily: 'var(--font-display)',
        fontStyle: 'italic',
        fontSize: 14,
        color: 'var(--muted)',
        marginTop: 12,
        marginBottom: 40,
        lineHeight: 1.6,
      }}>
        We use this to find your most resonant matches.
      </div>

      <form onSubmit={handleSubmit}>
        <div className="field-group">
          <label className="field-label">Date of birth</label>
          <div style={{ display: 'flex', gap: 10 }}>
            <select className="field" style={{ flex: 1 }} value={form.day} onChange={e => set('day', e.target.value)} required>
              <option value="">Day</option>
              {days.map(d => <option key={d} value={d}>{d}</option>)}
            </select>
            <select className="field" style={{ flex: '0 0 80px' }} value={form.month} onChange={e => set('month', e.target.value)} required>
              <option value="">Mo</option>
              {MONTHS.map((m, i) => <option key={i} value={i + 1}>{m}</option>)}
            </select>
            <select className="field" style={{ flex: '0 0 90px' }} value={form.year} onChange={e => set('year', e.target.value)} required>
              <option value="">Year</option>
              {years.map(y => <option key={y} value={y}>{y}</option>)}
            </select>
          </div>
        </div>

        <div className="field-group">
          <label className="field-label">Time of birth</label>
          {!form.unknownTime ? (
            <div style={{ display: 'flex', gap: 10 }}>
              <select className="field" style={{ flex: 1 }} value={form.hour} onChange={e => set('hour', e.target.value)}>
                {hours.map(h => <option key={h} value={h}>{String(h).padStart(2,'0')}:00</option>)}
              </select>
              <select className="field" style={{ flex: 1 }} value={form.min} onChange={e => set('min', e.target.value)}>
                {mins.map(m => <option key={m} value={m}>:{String(m).padStart(2,'0')}</option>)}
              </select>
            </div>
          ) : (
            <div style={{ color: 'var(--very-muted)', fontSize: 14, paddingTop: 10 }}>
              Defaulting to noon
            </div>
          )}
          <div style={{ marginTop: 10 }}>
            <label style={{ display: 'flex', alignItems: 'center', gap: 10, cursor: 'pointer' }}>
              <input
                type="checkbox"
                checked={form.unknownTime}
                onChange={e => set('unknownTime', e.target.checked)}
                style={{ accentColor: 'var(--gold)', width: 14, height: 14 }}
              />
              <span style={{ fontSize: 12, color: 'var(--muted)', fontWeight: 300 }}>
                I don't know my exact time
              </span>
            </label>
          </div>
        </div>

        <div className="field-group">
          <label className="field-label">City of birth</label>
          <LocationSearch
            placeholder={existingCoords && !birthCoords ? 'Leave blank to keep existing city' : 'Search city...'}
            showCurrentLocation={false}
            onSelect={(result) => setBirthCoords(result)}
          />
          <div style={{ fontSize: 11, color: 'var(--very-muted)', marginTop: 8, lineHeight: 1.5 }}>
            This is never shown to anyone.
          </div>
        </div>

        {error && <div className="error-msg">{error}</div>}

        <div style={{ marginTop: 32 }}>
          <button className="btn btn-gold" type="submit" disabled={loading}>
            {loading ? <span className="spinner" /> : fromSettings ? 'Save & exit' : 'Continue'}
          </button>
        </div>
      </form>
    </div>
  );
}
